import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Clock, MapPin, Sparkles, Feather, Flame,
  TreePine, Wind, Music, Heart, Sun, Moon, Compass, Leaf, Star, Quote,
} from "lucide-react";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

// ─── Hero + supporting imagery (curated from Holly's asset library) ──
const HERO_BG = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/nybdc4yh_Sage%20Jan%202023%202-46.jpg";
const FACILITATING = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/3wv6bhuj_PXL_20240904_161550046.PORTRAIT~4.jpg";
const BLISS_TENT = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/sraknrq4_1CB7E72D-B720-44B0-91EB-41A2A304155D_1_105_c.jpeg";
const NATURE = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/gg4x2k2h_6DC27C49-9DC2-4A41-B146-CBCE57337450_1_105_c.jpeg";

// ─── Data blocks ─────────────────────────────────────────────────────
const BENEFITS = [
  "Reduced stress and anxiety",
  "Improved relaxation and sleep",
  "Increased emotional resilience",
  "Greater mental clarity",
  "Relief from energetic stagnation",
  "Enhanced feelings of peace and wellbeing",
];

const INCLUDES = [
  { icon: Wind,     title: "Guided Full-Body Relaxation",   body: "We begin with a calming guided meditation designed to help you transition from the busy mind into a state of deep relaxation and presence." },
  { icon: Leaf,     title: "Customized Aromatherapy",       body: "Carefully selected essential oils chosen to support your emotional, mental, physical, and energetic wellbeing." },
  { icon: Compass,  title: "Energy Center Assessment",      body: "Each of the major energy centers, often called chakras, is assessed and energetically balanced to encourage healthy flow throughout the body." },
  { icon: Sparkles, title: "Reiki Healing",                 body: "The core Reiki session focuses on restoring energetic harmony and supporting your body's natural capacity for healing and renewal." },
  { icon: Music,    title: "Personal Sound Healing",        body: "Gentle sound-healing instruments are incorporated during in-person sessions to deepen relaxation and support energetic balance." },
  { icon: Flame,    title: "Sage Smudge Clearing",          body: "A traditional sage clearing helps refresh the energetic environment and create space for renewed clarity and calm." },
  { icon: Moon,     title: "Oracle Card Guidance",          body: "Each session concludes with an oracle card reading and intuitive reflection to provide support and insight." },
  { icon: Heart,    title: "Session Feedback",              body: "Before leaving, you receive personalized feedback regarding observations, energetic patterns, and recommendations for ongoing self-care." },
];

const AROMA_BENEFITS = [
  "Stress reduction", "Emotional balance", "Enhanced relaxation",
  "Mental clarity and focus", "Improved mood", "Support for healthy sleep habits",
  "Grounding during times of change",
];

const SESSIONS = [
  {
    slug: "private",
    title: "Private One-on-One Reiki",
    duration: "75 minutes",
    price: "$125",
    body: "Receive fully individualized support in a peaceful setting where every aspect of the experience is tailored specifically to your needs.",
  },
  {
    slug: "semi-private",
    title: "Semi-Private Reiki (2 people)",
    duration: "75 minutes",
    price: "$185 total",
    sub: "$92.50 per person",
    body: "Experience healing alongside a partner, friend, spouse, or family member while still receiving personalized attention and care.",
  },
  {
    slug: "group-3",
    title: "Small Group Reiki (3 people)",
    duration: "90 minutes",
    price: "$70 / person",
    body: "Perfect for friends, wellness groups, celebrations, retreats, or anyone who wishes to share a healing experience together.",
  },
  {
    slug: "group-4-5",
    title: "Small Group Reiki (4–6 people)",
    duration: "90 minutes",
    price: "$65 / person",
    body: "Bring your circle. Ideal for close friends, sisters, retreats, wellness offsites, or celebration gatherings.",
  },
  {
    slug: "long-distance",
    title: "Long Distance Virtual Reiki",
    duration: "60 minutes",
    price: "$85",
    body: "Receive Reiki from the comfort of your home through Google Meet. Includes guided relaxation, energy center assessment, oracle guidance, and personalized feedback. Sound healing is not included in virtual sessions due to audio limitations.",
  },
];

const BENEFICIARIES = [
  "Stress and overwhelm", "Anxiety", "Burnout", "Difficulty relaxing",
  "Emotional processing", "Life transitions", "Grief and loss",
  "Sleep challenges", "Personal growth", "Spiritual development",
  "Self-care and wellness maintenance",
];

const TESTIMONIALS = [
  {
    name: "Kathe Doremus",
    date: "September 2024",
    body: "I met Holly at a very transitional point in my life and thought it would be interesting to explore Reiki. My experience has been thoroughly amazing and enlightening — I have discovered depths of wisdom, clarity, and creativity that I thought I had lost in the growing-up-and-growing-old trajectory of life. Reiki, with Holly's guidance, has not only helped me find balance in my tumultuous world, but has reopened my imagination and eyes to the awe and magic that surround us in daily life. I cannot express enough admiration for Holly and her skills, knowledge, and the return to \"me\" that I have gained through the Reiki journey.",
  },
  {
    name: "Caitlin Leo",
    date: "June 2025",
    body: "I can't recommend this Reiki experience enough. From the moment you walk in, the space feels safe, welcoming, and deeply restorative. What sets this practice apart is the healing energy that infuses everything. The Reiki sessions are powerful and transformative, and taking the Reiki Level 1 course was a life-changing experience. I learned so much — not just about energy work, but about myself. The guidance was clear, compassionate, and empowering every step of the way. Whether you're looking to heal, learn, or rest in a supportive space, this is the place. It's a gift to be here.",
  },
];

const FAQS = [
  {
    q: "What should I wear to my Reiki session?",
    a: "Comfortable clothing that allows you to relax completely.",
  },
  {
    q: "Do I need experience with Reiki?",
    a: "Not at all. Many first-time clients begin their healing journey with Reiki.",
  },
  {
    q: "Will I be touched during the session?",
    a: "Reiki can be performed with light touch or with hands hovering above the body based on your comfort level and preferences. Most sessions are conducted with hands hovering over the energy centers.",
  },
  {
    q: "How often should I receive Reiki?",
    a: "Some clients book monthly wellness sessions while others schedule sessions during times of increased stress or transition on a weekly basis. Recommendations for follow-ups can be discussed during your session.",
  },
  {
    q: "What does Reiki feel like?",
    a: "Experiences vary. Some clients feel warmth, tingling, deep relaxation, emotional release, or simply a profound sense of calm. Some people see colors, flashes of light, or even have vivid daydreams that seem surreal. Some fall into a deep healing sleep. There is no right or wrong way to receive Reiki — the benefits are still occurring regardless of how you connect to the energy. The more frequently you connect, the more aware of the energy you will become.",
  },
  {
    q: "Can Reiki be done remotely?",
    a: "Yes. Reiki energy is not limited by physical location, which is why virtual distance sessions are available.",
  },
];

const REIKI_I_OUTCOMES = [
  "Receive Reiki I attunement from the Usui-Holy Fire III lineage",
  "Learn the history, principles, and ethics of Reiki",
  "Practice hand positions for self-healing and offering Reiki to others",
  "Understand chakras, the aura, and energy boundaries",
  "Practice scanning, beaming, and clearing techniques",
  "Receive a printed manual and a Reiki Level I practitioner certificate",
];

// ─────────────────────────────────────────────────────────────────────

export default function Healing() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Reiki Energy Healing Sessions",
    "serviceType": "Energy Healing",
    "description": "Reiki energy healing sessions with sound healing, aromatherapy, chakra balancing, and intuitive guidance in the Bliss Tent near Bliss Woods Forest Preserve, Sugar Grove, IL — plus virtual distance sessions worldwide.",
    "provider": { "@id": "https://avianayoga.com/#holly" },
    "areaServed": ["Sugar Grove, IL", "Aurora, IL", "Geneva, IL", "Naperville, IL", "Chicago, IL", "Illinois", "Online (Distance Reiki)"],
    "location": {
      "@type": "Place",
      "name": "The Bliss Tent · Aviana Yoga",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "290 Kedeka Rd",
        "addressLocality": "Sugar Grove",
        "addressRegion": "IL",
        "postalCode": "60554",
        "addressCountry": "US",
      },
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": TESTIMONIALS.length,
    },
    "review": TESTIMONIALS.map((t) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": t.name },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "reviewBody": t.body,
    })),
    "offers": [
      { "@type": "Offer", "name": "Private Reiki + Sound Healing — 75 min", "price": 125, "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Semi-Private Reiki — 75 min", "price": 185, "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Small Group Reiki (3) — 90 min", "price": 210, "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Small Group Reiki (4–6) — 90 min", "price": 260, "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Distance Virtual Reiki — 60 min", "price": 85, "priceCurrency": "USD" },
    ],
  };

  return (
    <div data-testid="healing-page">
      <SEO
        title="Reiki Energy Healing Sessions in Illinois · Book Online · Aviana Yoga"
        description="Reiki healing sessions in Sugar Grove, IL and virtual distance Reiki worldwide. Reduce stress, balance chakras, restore energy. In-person sessions in the Bliss Tent by Bliss Woods Forest Preserve. Private, semi-private, and small-group Reiki with sound healing and aromatherapy."
        path="/healing"
        image={HERO_BG}
        keywords="reiki near me, reiki sessions, reiki healing, energy healing, reiki practitioner, reiki therapy, reiki for stress relief, sound healing, distance reiki, virtual reiki session, reiki and aromatherapy, reiki in nature, holistic wellness, energy balancing, chakra balancing, reiki for emotional wellness, reiki healing Illinois, Sugar Grove reiki, Chicago reiki, reiki bliss tent"
        jsonLd={[faqJsonLd, serviceJsonLd]}
      />

      {/* ─── HERO (streamlined) ─── */}
      <section className="relative min-h-[72vh] flex items-center overflow-hidden text-aviana-cream" data-testid="healing-hero">
        <img src={HERO_BG} alt="Holly performing Reiki with sage" className="absolute inset-0 w-full h-full object-cover object-[60%_center] opacity-95" />
        <div className="absolute inset-0 bg-gradient-to-r from-aviana-plumDeep via-aviana-plumDeep/55 to-aviana-plumDeep/15" />
        <Mandala size={520} variant="full" className="absolute -left-32 top-[55%] text-aviana-gold opacity-[0.10] animate-spin-slow pointer-events-none" />
        <Mandala size={140} variant="lotus" className="absolute right-8 top-32 text-aviana-goldSoft/20 hidden md:block pointer-events-none" />
        <div className="container-aviana relative z-10 pt-32 pb-20">
          <p className="overline text-aviana-goldSoft mb-5">Reiki + Energy Healing · Sugar Grove, IL & Virtual</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[1.02] max-w-4xl">
            Reiki Energy Healing Sessions:
            <br />
            <em className="italic text-aviana-goldSoft">Restore Balance, Reduce Stress</em> & Reconnect with Yourself
          </h1>
          <p className="mt-8 font-serif italic text-aviana-cream/85 text-[19px] md:text-[21px] leading-[1.55] max-w-2xl">
            A gentle yet powerful pathway back to your center — in the Bliss Tent by Bliss Woods Forest Preserve, or virtually from wherever you are.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <Link to="/reiki" className="btn-gold" data-testid="healing-hero-cta">
              Book your Reiki session <ArrowRight size={14} />
            </Link>
            <a href="#invitation" className="text-aviana-goldSoft hover:text-aviana-cream text-[12px] tracking-[0.22em] uppercase">
              Read more ↓
            </a>
          </div>
        </div>
      </section>

      {/* ─── Invitation (the fuller hero copy, moved here) ─── */}
      <section id="invitation" className="py-20 md:py-24 relative" data-testid="healing-invitation">
        <Mandala size={340} variant="full" className="absolute -right-32 top-1/2 -translate-y-1/2 text-aviana-gold/[0.06] animate-spin-slow pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-3xl text-center relative">
          <GoldRule />
          <p className="overline-plum mt-8 mb-3">An invitation</p>
          <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-[1.15]">
            Step away from the noise. <em className="italic gold-text">Come home to yourself.</em>
          </h2>
          <div className="mt-8 space-y-5 font-serif text-aviana-inkSoft text-[17px] md:text-[18px] leading-[1.75]">
            <p>
              Enter a nurturing healing space designed to help you rest, reset, and reconnect. Whether
              you're navigating stress, anxiety, emotional overwhelm, burnout, life transitions, or simply
              seeking deeper balance, Reiki offers a gentle yet powerful pathway back to your center.
            </p>
            <p>
              Each session blends <strong className="text-aviana-plum not-italic">Reiki energy healing</strong>,
              guided relaxation, aromatherapy, sound healing, chakra balancing, and intuitive guidance — a
              deeply restorative experience tailored specifically to you.
            </p>
            <p className="text-aviana-plum italic">
              Located inside the Bliss Tent, surrounded by Bliss Woods Forest Preserve — your session
              becomes more than an appointment. It becomes a personal retreat.
            </p>
          </div>
          <GoldRule className="mt-10" />
        </div>
      </section>

      {/* ─── What Is Reiki? ─── */}
      <section className="py-24 md:py-28">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center max-w-6xl">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-20 blur-2xl" />
              <img src={FACILITATING} alt="Holly Johnson facilitating a Reiki energy healing session" className="relative rounded-[2.5rem] aspect-[3/4] object-cover w-full ring-1 ring-aviana-border shadow-xl" />
              <Mandala size={80} variant="lotus" className="absolute -bottom-6 -right-6 text-aviana-gold/80" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-3">What is Reiki?</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Understanding Reiki <em className="italic gold-text">energy healing.</em>
            </h2>
            <div className="mt-7 space-y-5 text-aviana-inkSoft text-[16px] leading-[1.8]">
              <p>
                Reiki is a gentle Japanese energy-healing practice that promotes physical relaxation,
                emotional balance, mental clarity, and overall wellbeing. During a Reiki session, energy
                is directed through the practitioner to support the body's natural healing processes and
                encourage a state of deep rest.
              </p>
              <p className="text-aviana-ink font-medium">Many clients report experiencing:</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-4">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[15px]">
                    <Check size={16} className="text-aviana-gold shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif italic text-aviana-plum text-[17px] pt-3">
                Reiki is non-invasive, fully clothed, and suitable for people at any stage of their wellness journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── The Bliss Tent Experience ─── */}
      <section className="py-24 md:py-28 bg-aviana-cream/40" data-testid="healing-bliss-tent">
        <div className="container-aviana max-w-6xl">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="overline-plum mb-3">A Healing Retreat in Nature</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              The <em className="italic gold-text">Bliss Tent</em> Experience
            </h2>
          </div>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="space-y-5 text-aviana-inkSoft text-[16px] leading-[1.8]">
                <p>
                  Your Reiki session takes place inside the <strong>Bliss Tent</strong>, a spacious 20-foot
                  canvas bell tent designed to provide a peaceful sanctuary away from the demands of daily life.
                </p>
                <p>
                  Nestled on a private homestead beside <strong>Bliss Woods Forest Preserve</strong>, the setting
                  offers a rare opportunity to disconnect from constant stimulation and reconnect with nature.
                </p>
                <p className="text-aviana-ink font-medium pt-1">As you settle into your session, you may hear:</p>
                <ul className="space-y-2">
                  {["Birds singing in the trees", "Wind moving through the forest", "The calming sounds of nature surrounding you"].map((line) => (
                    <li key={line} className="flex gap-3">
                      <TreePine size={16} className="text-aviana-gold shrink-0 mt-1" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  During cooler months, the tent is comfortably heated with a wood-burning stove, creating
                  a warm and cozy atmosphere. The gentle crackle of the fire adds another layer of comfort,
                  helping you fully relax and receive.
                </p>
                <p className="text-aviana-ink font-medium pt-1">The homestead also features:</p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {["Seasonal gardens", "Open natural spaces", "A friendly flock of hens who often greet visitors"].map((line) => (
                    <li key={line} className="flex items-start gap-2 text-[15px]">
                      <Feather size={15} className="text-aviana-gold shrink-0 mt-1" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-serif italic text-aviana-plum text-[17px] pt-3">
                  Many clients describe the drive to the Bliss Tent as the beginning of their healing
                  experience — stress melting away before they even arrive.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-20 blur-2xl" />
                <img src={BLISS_TENT} alt="The Bliss Tent — a 20-foot canvas bell tent for Reiki sessions in Sugar Grove, IL" className="relative rounded-[2.5rem] aspect-[4/5] object-cover w-full ring-1 ring-aviana-border shadow-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Location / Map ─── */}
      <section className="py-24 md:py-28 relative overflow-hidden" data-testid="healing-location">
        <Mandala size={280} variant="lotus" className="absolute -right-10 top-14 text-aviana-gold/[0.06] hidden md:block pointer-events-none" />
        <div className="container-aviana max-w-6xl relative">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="overline-plum mb-3">Find us</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Where the <em className="italic gold-text">Bliss Tent</em> lives.
            </h2>
            <p className="mt-5 text-aviana-muted text-[15px] leading-[1.7]">
              Just off the edge of Bliss Woods Forest Preserve — a short, scenic drive from Aurora, Geneva,
              Naperville, and greater Chicagoland.
            </p>
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-8 order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-aviana-border shadow-sm aspect-[16/10]">
                <iframe
                  title="Aviana Yoga · Bliss Tent Location"
                  src="https://maps.google.com/maps?q=290+Kedeka+Rd+Sugar+Grove+IL&z=14&output=embed"
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="healing-map-iframe"
                />
              </div>
            </div>
            <div className="lg:col-span-4 order-1 lg:order-2">
              <div className="bg-white border border-aviana-border rounded-3xl p-7 md:p-8 shadow-sm h-full flex flex-col">
                <MapPin size={22} className="text-aviana-gold mb-3" />
                <p className="overline-plum">Address</p>
                <p className="font-display text-2xl text-aviana-ink mt-2 leading-tight">
                  290 Kedeka Rd
                  <br />
                  Sugar Grove, IL 60554
                </p>
                <p className="mt-5 text-[14.5px] text-aviana-inkSoft leading-[1.7]">
                  Please <strong>park alongside the driveway</strong> when you arrive. This is a private residence
                  and there may be another session scheduled just before yours — kindly plan to arrive
                  <strong> on time or no more than 5 minutes early</strong> so everyone's experience stays
                  peaceful and undisturbed. Holly will greet you at the tent.
                </p>
                <div className="mt-auto pt-6 flex flex-col gap-3">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=290+Kedeka+Rd+Sugar+Grove+IL+60554"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-center"
                    data-testid="healing-directions-cta"
                  >
                    Get directions <ArrowRight size={14} />
                  </a>
                  <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted text-center">
                    Approx. 55 min from downtown Chicago
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What's Included In Every Session ─── */}
      <section className="py-24 md:py-28">
        <div className="container-aviana max-w-6xl">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="overline-plum mb-3">What's included</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Your customized <em className="italic gold-text">healing experience.</em>
            </h2>
            <p className="mt-5 text-aviana-muted text-[15px] leading-[1.7]">
              Every session is uniquely tailored to your needs while including these therapeutic elements.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {INCLUDES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white rounded-2xl border border-aviana-border p-7 flex gap-5" data-testid={`healing-include-${title.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <div className="w-12 h-12 rounded-full bg-aviana-gold/15 text-aviana-gold flex items-center justify-center flex-shrink-0">
                  <Icon size={20} />
                </div>
                <div>
                  <h3 className="font-display text-xl text-aviana-ink">{title}</h3>
                  <p className="mt-2 text-aviana-inkSoft text-[14.5px] leading-[1.7]">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Aromatherapy Section ─── */}
      <section className="py-24 md:py-28 bg-aviana-paper">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 items-center max-w-6xl">
          <div className="lg:col-span-6">
            <p className="overline-plum mb-3">Aromatherapy</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              The benefits of <em className="italic gold-text">aromatherapy.</em>
            </h2>
            <div className="mt-7 space-y-4 text-aviana-inkSoft text-[16px] leading-[1.8]">
              <p>
                Aromatherapy enhances your Reiki experience by engaging the body's olfactory system,
                supporting relaxation and emotional wellbeing on multiple levels.
              </p>
              <p className="text-aviana-ink font-medium pt-1">Benefits may include:</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                {AROMA_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[15px]">
                    <Leaf size={15} className="text-aviana-gold shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="pt-2">
                Essential oils are thoughtfully selected based on your unique intentions and goals for
                the session.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white rounded-3xl border border-aviana-border p-8 md:p-10 shadow-sm">
              <p className="overline-plum mb-3">Continue your journey at home</p>
              <h3 className="font-display text-2xl md:text-3xl text-aviana-ink leading-tight">
                The same high-quality oils used in your session.
              </h3>
              <p className="mt-4 text-aviana-inkSoft text-[15px] leading-[1.75]">
                Interested in incorporating the same essential oils into your daily routine? Explore
                Young Living Essential Oils — Holly's trusted source for therapeutic-grade botanical support.
              </p>
              <a
                href="https://www.youngliving.com/us/en/referral/777127"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-6 inline-flex"
                data-testid="healing-young-living-link"
              >
                Shop Young Living Oils <ArrowRight size={14} />
              </a>
              <p className="mt-3 text-[11px] tracking-wide text-aviana-muted">
                Affiliate link — supports Aviana Yoga at no extra cost to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Session Options ─── */}
      <section id="sessions" className="py-24 md:py-28 bg-aviana-cream/40 relative overflow-hidden" data-testid="healing-sessions">
        <Mandala size={340} variant="full" className="absolute -right-32 top-20 text-aviana-gold/[0.07] animate-spin-slow pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-6xl relative">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="overline-plum mb-3">Session options</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Choose the Reiki experience <em className="italic gold-text">that's right for you.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {SESSIONS.map((s) => (
              <div key={s.slug} className="bg-white rounded-2xl border border-aviana-border p-7 flex flex-col" data-testid={`healing-session-${s.slug}`}>
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h3 className="font-display text-xl text-aviana-ink">{s.title}</h3>
                  <div className="text-right">
                    <div className="font-display text-2xl text-aviana-plum">{s.price}</div>
                    {s.sub && <div className="text-[11px] uppercase tracking-wider text-aviana-muted">{s.sub}</div>}
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-aviana-muted mt-2">
                  <Clock size={12} className="text-aviana-gold" /> {s.duration}
                </div>
                <p className="mt-4 text-aviana-inkSoft text-[14.5px] leading-[1.7] flex-1">{s.body}</p>
                <Link
                  to="/reiki"
                  className="mt-6 inline-flex items-center gap-1.5 text-[12px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-gold"
                  data-testid={`healing-session-cta-${s.slug}`}
                >
                  Book this session <ArrowRight size={13} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/reiki" className="btn-primary" data-testid="healing-view-availability">
              View availability & book online <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="py-24 md:py-28 bg-aviana-paper relative overflow-hidden" data-testid="healing-testimonials">
        <Mandala size={520} variant="full" className="absolute -left-40 -bottom-40 text-aviana-gold/[0.06] animate-spin-slow pointer-events-none" />
        <Mandala size={280} variant="lotus" className="absolute right-8 top-8 text-aviana-plum/[0.07] hidden md:block pointer-events-none" />
        <div className="container-aviana max-w-5xl relative">
          <div className="text-center mb-14">
            <Quote size={28} className="text-aviana-gold mx-auto mb-4" />
            <p className="overline-plum mb-3">Client stories</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              What clients are <em className="italic gold-text">saying.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                className="bg-white border border-aviana-border rounded-3xl p-8 md:p-9 shadow-sm relative"
                data-testid={`healing-testimonial-${i}`}
              >
                <div className="flex gap-1 text-aviana-gold mb-4" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="text-aviana-inkSoft text-[15.5px] leading-[1.85] font-serif italic">
                  "{t.body}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-aviana-border flex items-baseline justify-between">
                  <span className="font-display text-lg text-aviana-plum">— {t.name}</span>
                  <span className="text-[11px] tracking-[0.2em] uppercase text-aviana-muted">{t.date}</span>
                </figcaption>
                <Mandala size={44} variant="lotus" className="absolute -top-3 -right-3 text-aviana-gold/60" />
              </figure>
            ))}
          </div>
          <div className="text-center mt-12">
            <a
              href="https://www.google.com/search?q=Aviana+Yoga+Sugar+Grove+IL+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-gold inline-flex items-center gap-2"
              data-testid="healing-google-reviews-link"
            >
              Read more on Google <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Who Can Benefit ─── */}
      <section className="py-24 md:py-28 relative">
        <Mandala size={220} variant="lotus" className="absolute right-6 top-16 text-aviana-gold/[0.07] hidden md:block pointer-events-none" />
        <Mandala size={220} variant="lotus" className="absolute left-6 bottom-16 text-aviana-plum/[0.06] hidden md:block pointer-events-none" />
        <div className="container-aviana max-w-4xl text-center">
          <p className="overline-plum mb-3">Who Reiki serves</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
            Reiki may be <em className="italic gold-text">helpful for…</em>
          </h2>
          <p className="mt-5 text-aviana-muted text-[15px]">People seeking support with:</p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            {BENEFICIARIES.map((b) => (
              <span
                key={b}
                className="px-4 py-2 rounded-full bg-aviana-cream/60 border border-aviana-border text-aviana-ink text-[13px]"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-24 md:py-28 bg-aviana-paper relative overflow-hidden" data-testid="healing-faq">
        <Mandala size={280} variant="lotus" className="absolute -left-16 top-12 text-aviana-gold/[0.06] pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-3xl relative">
          <div className="text-center mb-14">
            <p className="overline-plum mb-3">FAQ</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Frequently asked <em className="italic gold-text">questions.</em>
            </h2>
            <GoldRule className="mt-8" />
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="group bg-white rounded-2xl border border-aviana-border p-6 open:shadow-sm transition-shadow" data-testid={`healing-faq-${i}`}>
                <summary className="font-display text-lg md:text-xl text-aviana-ink cursor-pointer list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-aviana-gold group-open:rotate-45 transition-transform text-2xl leading-none shrink-0">+</span>
                </summary>
                <p className="mt-4 text-aviana-inkSoft text-[15px] leading-[1.75]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final Conversion ─── */}
      <section className="py-28 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="healing-final-cta">
        <img src={NATURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep/70 to-aviana-plumDeep" />
        <Mandala size={620} variant="full" className="absolute -right-40 top-1/4 text-aviana-goldSoft/10 animate-spin-slow pointer-events-none hidden md:block" />
        <div className="container-aviana relative max-w-3xl text-center">
          <Sparkles size={24} className="text-aviana-goldSoft mx-auto mb-4" />
          <p className="overline text-aviana-goldSoft mb-4">One breath at a time</p>
          <h2 className="font-display text-4xl md:text-6xl text-aviana-cream leading-[1.02]">
            Give yourself permission to <em className="italic text-aviana-goldSoft">pause.</em>
          </h2>
          <div className="mt-8 space-y-5 font-serif italic text-aviana-cream/85 text-[17px] leading-[1.65] max-w-2xl mx-auto">
            <p>
              In a world that constantly asks you to do more, Reiki offers an opportunity to simply be.
            </p>
            <p>
              Let the sounds of the forest, the comfort of the Bliss Tent, customized aromatherapy,
              restorative Reiki, and intentional guidance help you reconnect with the calm, clarity, and
              balance already within you.
            </p>
            <p>
              Whether you choose an in-person experience in nature or a virtual session from home, you
              deserve dedicated time to rest, receive, and heal.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/reiki" className="btn-gold" data-testid="healing-final-cta-button">
              Book your Reiki session today <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── REIKI LEVEL I TRAINING (kept from previous page) ─── */}
      <section id="training" className="py-28 bg-aviana-paper relative overflow-hidden">
        <div className="container-aviana relative max-w-5xl">
          <div className="text-center mb-14">
            <p className="overline-plum mb-4">Practitioner Trainings</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.02]">
              Reiki Level <em className="italic gold-text">I</em> Training & Attunement
            </h2>
            <p className="mt-6 font-serif italic text-aviana-muted text-[16px] leading-[1.55] max-w-2xl mx-auto">
              Step into a lineage of light. Become the steady, clear vessel that you have always been at
              your essence — and learn to share Reiki with yourself, your loved ones, and your circle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-14 max-w-3xl mx-auto">
            <TrainingStat icon={Clock} label="Duration" value="2 days · ~14 hours" />
            <TrainingStat icon={MapPin} label="Location" value="Sugar Grove, IL" />
          </div>
          <div className="text-center mb-14">
            <p className="overline-plum mb-2">Investment</p>
            <p className="text-aviana-ink text-2xl md:text-3xl font-display">$444</p>
            <p className="text-aviana-muted text-[13px] mt-1">Sisterhood rates available · limited to 6 students</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div className="bg-white border border-aviana-border rounded-2xl p-8">
              <p className="overline-plum mb-4">What you'll receive</p>
              <ul className="space-y-3">
                {REIKI_I_OUTCOMES.map((line) => (
                  <li key={line} className="flex gap-3 text-aviana-inkSoft text-[14.5px] leading-[1.65]">
                    <Check size={16} className="text-aviana-gold shrink-0 mt-1" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-aviana-border rounded-2xl p-8">
              <p className="overline-plum mb-4">Who this is for</p>
              <div className="space-y-4 text-aviana-inkSoft text-[14.5px] leading-[1.75]">
                <p>This training is open to anyone called to the work — no prior energy-healing experience required. You'll leave certified to offer Reiki to yourself, friends and family, your own clients in a complementary practice, or simply to deepen your own daily spiritual hygiene.</p>
                <p>Many graduates use Reiki I as the foundation for becoming yoga teachers, massage therapists, or simply more present partners and parents. The attunement itself is permanent.</p>
                <p className="font-serif italic text-aviana-plum text-[15px]">
                  "She finally remembered what her hands were for." — a Reiki I graduate
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a href="mailto:avianayoga@gmail.com?subject=Reiki%20Level%20I%20Training%20-%20Inquiry" className="btn-gold" data-testid="reiki-i-cta">
              Inquire about the next cohort <ArrowRight size={14} />
            </a>
            <p className="mt-4 text-[12px] text-aviana-muted tracking-[0.22em] uppercase">Limited to 6 students per training</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrainingStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-aviana-border rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-aviana-gold/15 text-aviana-gold flex items-center justify-center flex-shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <p className="overline-plum text-[10px]">{label}</p>
        <p className="text-aviana-ink text-lg md:text-xl font-display mt-1.5">{value}</p>
      </div>
    </div>
  );
}

// Slim horizontal gold divider with a lotus mandala centered on it —
// used to visually punctuate long-form sections without heavy typography.
function GoldRule({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`} aria-hidden>
      <span className="h-px w-14 md:w-20 bg-gradient-to-r from-transparent to-aviana-gold/60" />
      <Mandala size={22} variant="lotus" className="text-aviana-gold" />
      <span className="h-px w-14 md:w-20 bg-gradient-to-l from-transparent to-aviana-gold/60" />
    </div>
  );
}
